import { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { TelegramIcon } from "./TelegramButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const openEnquiryChat = () => window.dispatchEvent(new Event("open-enquiry-chat"));

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };
const MIN_CHARS = 15;
const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
const label = "text-[10px] tracking-[0.18em] uppercase text-slate-500";

export const EnquiryChatModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);
  const [staff, setStaff] = useState(null);

  useEffect(() => {
    const onOpen = () => {
      setForm(EMPTY); setOtpSent(false); setOtp(""); setToken(null); setVerifiedEmail(""); setDone(null); setBusy(""); setStaff(null);
      setOpen(true);
    };
    window.addEventListener("open-enquiry-chat", onOpen);
    return () => window.removeEventListener("open-enquiry-chat", onOpen);
  }, []);

  if (!open) return null;

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "email" && v.trim().toLowerCase() !== verifiedEmail) { setToken(null); setOtpSent(false); setOtp(""); setStaff(null); }
  };
  const verified = !!token && form.email.trim().toLowerCase() === verifiedEmail;
  const staffShown = verified && !!staff;
  const locked = staffShown && staff.letter_found;
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());
  const charCount = form.message.length;
  const messageOk = charCount >= MIN_CHARS;

  const sendOtp = async () => {
    if (!emailOk) { toast.error("Enter a valid email address first."); return; }
    setBusy("otp");
    try {
      await axios.post(`${API}/chat/otp/send`, { email: form.email.trim() });
      setOtpSent(true); setOtp("");
      toast.success("Verification code sent — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the code. Please check the email address.");
    } finally { setBusy(""); }
  };

  const verifyOtp = async () => {
    if (otp.trim().length !== 6) return;
    setBusy("verify");
    try {
      const { data } = await axios.post(`${API}/chat/otp/verify`, { email: form.email.trim(), code: otp.trim() });
      setToken(data.token); setVerifiedEmail(form.email.trim().toLowerCase());
      if (data.staff) {
        setStaff(data.staff);
        setForm((f) => ({ ...f, name: data.staff.name || f.name, phone: data.staff.cug_mobile || f.phone,
          employee_id: data.staff.employee_id || "", department: data.staff.department || "", designation: data.staff.designation || "" }));
      } else setStaff(null);
      toast.success("Email verified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect or expired code.");
    } finally { setBusy(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!verified) { toast.error("Please verify your email with the OTP before submitting."); return; }
    if (!messageOk) { toast.error(`Please describe your enquiry in at least ${MIN_CHARS} characters so we can assist you better.`); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Please enter a valid 10-digit phone number."); return; }
    setBusy("submit");
    try {
      const { data } = await axios.post(`${API}/enquiry-chat`, { token, name: form.name, phone: form.phone, subject: form.subject, message: form.message, email: form.email.trim(),
        employee_id: form.employee_id || "", department: form.department || "", designation: form.designation || "" });
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your enquiry. Please try again.");
      if (err?.response?.status === 400) { setToken(null); setOtpSent(false); setOtp(""); }
    } finally { setBusy(""); }
  };

  return (
    <div data-lenis-prevent className="fixed inset-0 z-[9000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="enquiry-chat-modal">
      <div className="w-full max-w-lg bg-nx-navy border border-white/15 shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-nx-goldlight">Chat With Us</p>
            <h3 className="text-white text-lg font-medium mt-1">Tell us how we can help</h3>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close" data-testid="enquiry-chat-close"><X size={18} /></button>
        </div>

        {done ? (
          <div className="px-6 py-8 flex flex-col items-center text-center gap-4" data-testid="enquiry-chat-success">
            <CheckCircle2 size={40} className="text-emerald-400" />
            {!done.staff_holiday && <p className="text-white font-medium" data-testid="enquiry-chat-success-text">{done.salutation ? `${done.salutation}. Your enquiry has been received.` : `Thank you, ${form.name.trim()}. Your enquiry has been received.`}</p>}
            {done.holiday && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/40 px-4 py-3" data-testid={done.staff_holiday ? "enquiry-chat-staff-holiday-notice" : "enquiry-chat-holiday-notice"}>{done.holiday_message}</p>}
            {done.staff_holiday && (
              <a href={done.telegram_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1B8CC2] text-white font-semibold rounded-full text-sm px-6 py-3 transition-colors" data-testid="enquiry-chat-telegram-link">
                <TelegramIcon size={16} /> Continue on Telegram
              </a>
            )}
            {!done.holiday && (
              <>
                <p className="text-sm text-slate-400">Our team will review it right away. To continue this conversation live, open our Telegram support chat — you'll be connected to the right department there.</p>
                <a href={done.telegram_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1B8CC2] text-white font-semibold rounded-full text-sm px-6 py-3 transition-colors" data-testid="enquiry-chat-telegram-link">
                  <TelegramIcon size={16} /> Continue on Telegram
                </a>
              </>
            )}
            <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-white uppercase tracking-[0.14em]" data-testid="enquiry-chat-done">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 flex flex-col gap-4">
            {staffShown && (
              <div className="border border-nx-gold/40 bg-nx-gold/[0.08] px-4 py-2.5 text-xs text-nx-goldlight flex items-center gap-2" data-testid="enquiry-chat-staff-banner">
                <ShieldCheck size={14} /> {locked ? "Staff record verified — your details are filled from our records and locked." : "Staff email verified — your name is filled from our records. Please add your Employee ID, Department and Designation."}
              </div>
            )}
            <label className="block"><span className={label}>Full Name</span>
              <input required maxLength={120} value={form.name} onChange={set("name")} placeholder="Your full name" readOnly={staffShown} className={`${input} mt-1 ${staffShown ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="enquiry-chat-name" /></label>

            <div>
              <span className={label}>Email {verified && <span className="text-emerald-400 normal-case tracking-normal ml-2 inline-flex items-center gap-1"><ShieldCheck size={12} /> verified</span>}</span>
              <div className="mt-1 flex gap-2">
                <input required type="email" maxLength={160} value={form.email} onChange={set("email")} placeholder="you@company.com" readOnly={verified} className={`${input} ${verified ? "opacity-70" : ""}`} data-testid="enquiry-chat-email" />
                {!verified && (
                  <button type="button" onClick={sendOtp} disabled={busy === "otp" || !emailOk} className="shrink-0 text-[11px] tracking-[0.12em] uppercase border border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/15 px-3 py-2 disabled:opacity-40 transition-colors" data-testid="enquiry-chat-send-otp">
                    {busy === "otp" ? "Sending…" : otpSent ? "Resend OTP" : "Send OTP"}
                  </button>
                )}
              </div>
              {otpSent && !verified && (
                <div className="mt-2 flex gap-2" data-testid="enquiry-chat-otp-row">
                  <input inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code from your email" className={`${input} tracking-[0.3em]`} data-testid="enquiry-chat-otp" />
                  <button type="button" onClick={verifyOtp} disabled={busy === "verify" || otp.length !== 6} className="shrink-0 text-[11px] tracking-[0.12em] uppercase bg-nx-gold text-nx-navy font-semibold px-4 py-2 disabled:opacity-40" data-testid="enquiry-chat-verify-otp">
                    {busy === "verify" ? "Checking…" : "Verify"}
                  </button>
                </div>
              )}
              {!verified && <p className="mt-1.5 text-[11px] text-slate-500">We'll email you a one-time code to confirm this address before you can submit.</p>}
            </div>

            {staffShown && (
              <div className="grid grid-cols-3 gap-3" data-testid="enquiry-chat-staff-fields">
                <label className="block"><span className={label}>Employee ID</span>
                  <input readOnly={locked} maxLength={40} value={form.employee_id || ""} onChange={set("employee_id")} placeholder="e.g. NST-00012" className={`${input} mt-1 ${locked ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="enquiry-chat-employee-id" /></label>
                <label className="block"><span className={label}>Department</span>
                  <input readOnly={locked} maxLength={120} value={form.department || ""} onChange={set("department")} placeholder="Department" className={`${input} mt-1 ${locked ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="enquiry-chat-department" /></label>
                <label className="block"><span className={label}>Designation</span>
                  <input readOnly={locked} maxLength={120} value={form.designation || ""} onChange={set("designation")} placeholder="Designation" className={`${input} mt-1 ${locked ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="enquiry-chat-designation" /></label>
              </div>
            )}
            <label className="block"><span className={label}>{staffShown ? "Company CUG Mobile Number" : "Phone Number"}</span>
              <input required type="tel" maxLength={24} value={form.phone} onChange={set("phone")} placeholder="+91 98xxx xxxxx" readOnly={!!(staffShown && staff.cug_mobile)} className={`${input} mt-1 ${staffShown && staff.cug_mobile ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="enquiry-chat-phone" /></label>
            <label className="block"><span className={label}>Subject</span>
              <input required maxLength={160} value={form.subject} onChange={set("subject")} placeholder="What is this about?" className={`${input} mt-1`} data-testid="enquiry-chat-subject" /></label>
            <label className="block"><span className={label}>Message</span>
              <textarea required rows={4} maxLength={4000} value={form.message} onChange={set("message")} placeholder="How can we help? (minimum 15 characters)" className={`${input} mt-1 resize-none`} data-testid="enquiry-chat-message" />
              <span className="mt-1.5 flex items-center justify-between gap-3 text-[11px]">
                {!messageOk && form.message ? <span className="text-amber-300" data-testid="enquiry-chat-message-hint">Please describe your enquiry in at least 15 characters so we can assist you better.</span> : <span className="text-slate-500">Minimum 15 characters.</span>}
                <span className={`tabular-nums shrink-0 ${messageOk ? "text-emerald-400" : "text-slate-500"}`} data-testid="enquiry-chat-char-count">{charCount} / {MIN_CHARS} characters</span>
              </span></label>

            <button type="submit" disabled={!verified || !messageOk || busy === "submit"} className="inline-flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="enquiry-chat-submit">
              {busy === "submit" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {!verified ? "Verify email to submit" : !messageOk ? `Add ${MIN_CHARS - charCount} more character${MIN_CHARS - charCount === 1 ? "" : "s"} to submit` : "Submit Enquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
